<template>
  <div
    class="bottom-sheet-root"
    :data-elevated-layer="elevated ? '' : undefined"
  >
    <Transition name="bottom-sheet-backdrop">
      <div
        v-if="open && backdrop"
        sheet-backdrop
        :class="[
          'fixed inset-0 bg-gray-900/50 dark:bg-gray-900/80',
          band.backdrop
        ]"
        @click="close"
      ></div>
    </Transition>
    <Transition name="bottom-sheet-slide" @after-leave="emit('hide-sheet')">
      <div
        v-if="open"
        role="dialog"
        aria-modal="true"
        :class="panelClasses"
        :style="panelStyle"
        @keydown.escape="onPanelEscape"
      >
        <div class="p-4 rounded-t-lg bg-white dark:bg-gray-800 z-20">
          <div class="flex items-center gap-3 min-h-8">
            <button
              type="button"
              class="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-transparent text-sm text-gray-400 hover:bg-gray-200 hover:text-gray-900 dark:hover:bg-gray-600 dark:hover:text-white cursor-pointer"
              @click="close"
            >
              <CloseIcon class="w-4" />
              <span class="sr-only">Close</span>
            </button>
            <slot name="title">
              <h5 v-if="title" class="text-base font-semibold text-gray-900">
                {{ title }}
              </h5>
            </slot>
            <slot name="action"></slot>
          </div>
          <slot name="header"></slot>
        </div>
        <div class="overflow-y-auto min-h-0 pb-[env(safe-area-inset-bottom)]">
          <slot></slot>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { visualViewportRect } from "@/lib/anchoredPosition";

import CloseIcon from "@/images/x.svg?component";

// A modal sheet that slides up and fills the screen below a small gap. The
// header holds the close icon, the title, an optional action on the right and
// whatever the `header` slot adds, such as a search field; the body scrolls. Nothing is
// sized from the soft keyboard on purpose: the keyboard simply covers the lower
// part of the body, and everything a user must reach stays in the header.
// Mounts closed and slides up; closes from the close icon, the backdrop, Escape
// or `close()`, and emits `hide-sheet` once the leave transition has played so the
// parent can unmount.

const emit = defineEmits<{
  (e: "show-sheet"): void;
  (e: "hide-sheet"): void;
}>();

const props = withDefaults(
  defineProps<{
    title?: string;
    backdrop?: boolean;
    // Lifts the sheet above every dialog band (Seranova modals at 1020/1030,
    // Bootstrap modals at 1050, tippy popovers at 9999) for a sheet opened
    // from inside one, and marks the root as an elevated layer so popovers
    // beneath stay open while the user presses inside it
    // (lib/popoverOutsidePress).
    elevated?: boolean;
  }>(),
  { title: "", backdrop: true, elevated: false }
);

// 1002/1003 sits above the mobile nav drawer (z-[1001], fixed only below `md`)
// and below spinners, modals, flyouts and datepickers. 10000/10001 clears the
// tippy popovers at 9999 so their host is dimmed like everything else.
// Placeholders for TB-2193's band map.
const BANDS = {
  sheet: { panel: "z-[1003]", backdrop: "z-[1002]" },
  elevated: { panel: "z-[10001]", backdrop: "z-[10000]" }
};

const band = computed(() => (props.elevated ? BANDS.elevated : BANDS.sheet));

// A complete literal for Tailwind's scanner. The top carries the pan offset
// through a custom property because Tailwind runs with `important: true`, so
// an inline `top` would lose to the utility.
const PANEL =
  "fixed left-0 right-0 bottom-0 top-[calc(env(safe-area-inset-top)+1.5rem+var(--sheet-pan,0px))] w-full rounded-t-lg overflow-x-hidden bg-white dark:bg-gray-800 grid grid-rows-[auto_1fr] bottom-sheet";

const panelClasses = computed(() => [PANEL, band.value.panel]);

// The sheet's top follows the visible area's top, not the layout's. iOS
// Safari pans the visible area down over the layout when the keyboard opens
// for a field inside a fixed element, and a header pinned to the layout's top
// would scroll off screen with it; browsers that never pan report 0. Only the
// offset is used: height stays the layout's, and the keyboard covers the
// lower rows. The gap leaves a strip of the dimmed page visible below the
// status bar (and tappable to close).
const visibleTop = ref(0);

const measureVisibleTop = () => {
  visibleTop.value = visualViewportRect().top;
};

let frame: number | null = null;
const scheduleMeasure = () => {
  if (frame !== null) return;
  frame = requestAnimationFrame(() => {
    frame = null;
    measureVisibleTop();
  });
};

const panelStyle = computed(() => ({
  "--sheet-pan": `${Math.round(visibleTop.value)}px`
}));

// The panel is a v-if child of <Transition>, so Vue owns its mount and
// unmount: closing flips `open`, the leave transition plays, @after-leave
// emits hide-sheet for the parent to unmount.
const open = ref(false);

const close = () => {
  open.value = false;
};

// A sheet can open on top of a drawer or a modal that also closes on a
// document-level Escape. With focus inside the sheet the key reaches the panel
// first; closing here and stopping the event keeps the host open. The
// document listener stays as the fallback for a sheet focus never entered.
const onDocumentKeydown = (event: KeyboardEvent) => {
  if (event.key === "Escape") close();
};

const onPanelEscape = (event: KeyboardEvent) => {
  event.stopPropagation();
  close();
};

defineExpose({ close });

onMounted(() => {
  document.addEventListener("keydown", onDocumentKeydown);
  measureVisibleTop();
  window.visualViewport?.addEventListener("resize", scheduleMeasure);
  window.visualViewport?.addEventListener("scroll", scheduleMeasure, {
    passive: true
  });
  // Mount closed, then flip open so the slide-in plays from off screen.
  open.value = true;
  emit("show-sheet");
});

onBeforeUnmount(() => {
  document.removeEventListener("keydown", onDocumentKeydown);
  window.visualViewport?.removeEventListener("resize", scheduleMeasure);
  window.visualViewport?.removeEventListener("scroll", scheduleMeasure);
  if (frame !== null) cancelAnimationFrame(frame);
});
</script>

<style lang="scss">
.bottom-sheet-slide-enter-active,
.bottom-sheet-slide-leave-active {
  transition: transform 0.3s ease-in-out;
}
.bottom-sheet-slide-enter-from,
.bottom-sheet-slide-leave-to {
  transform: translateY(100%);
}
.bottom-sheet-backdrop-enter-active,
.bottom-sheet-backdrop-leave-active {
  transition: opacity 0.3s ease;
}
.bottom-sheet-backdrop-enter-from,
.bottom-sheet-backdrop-leave-to {
  opacity: 0;
}
</style>
